/**
 * PURE. Skeleton builder (creatures §B2–§B4, §B9): the 35-bone core in the art §5.4 canon, the digitigrade variant, the
 * tail rest S/J curve, the optional chains through the palette allocator (§B3, 48-bone cap, rigid fallback), sockets and
 * trophy sockets. Bind orientation is identity everywhere (§B1); a bone is its model-space pivot.
 *
 * Units: H. The skeleton is first laid out from the sliders, then uniformly normalized so the crown of the skull sits at
 * exactly y = 1.0 with the soles at y = 0 (art §0: H is the ground-to-crown height). The «Рост» (height) slider is NOT baked:
 * it is the root scale (`RootXform.scale`, creatures §B4 "uniform scale at root").
 */
import { MAX_BONES, type BoneDef, type BoneName, type Rig, type SocketName } from '../../shared';
import type { CreatureSpec } from '../dna/resolve';
import { baseFace, eyeGeom, type EyeGeom, type FaceRigInfo } from './face';
import { skullProfile, type SkullProfile } from './skull';

export type V3 = [number, number, number];

/** Core palette order P0–P34 (creatures §B2). */
export const CORE_BONES: readonly BoneName[] = [
  'pelvis', 'spine_01', 'spine_02', 'belly', 'neck', 'head', 'jaw', 'mouth', 'nose', 'eye_L', 'eye_R', 'lid_L', 'lid_R', 'brow_L', 'brow_R',
  'ear_L_01', 'ear_L_02', 'ear_R_01', 'ear_R_02', 'clavicle_L', 'upperarm_L', 'forearm_L', 'hand_L', 'clavicle_R', 'upperarm_R', 'forearm_R', 'hand_R',
  'thigh_L', 'calf_L', 'foot_L', 'toe_L', 'thigh_R', 'calf_R', 'foot_R', 'toe_R',
];
const CORE_PARENT: Readonly<Record<string, string | null>> = {
  pelvis: null, spine_01: 'pelvis', spine_02: 'spine_01', belly: 'spine_01', neck: 'spine_02', head: 'neck', jaw: 'head', mouth: 'head', nose: 'head',
  eye_L: 'head', eye_R: 'head', lid_L: 'head', lid_R: 'head', brow_L: 'head', brow_R: 'head', ear_L_01: 'head', ear_L_02: 'ear_L_01', ear_R_01: 'head',
  ear_R_02: 'ear_R_01', clavicle_L: 'spine_02', upperarm_L: 'clavicle_L', forearm_L: 'upperarm_L', hand_L: 'forearm_L', clavicle_R: 'spine_02',
  upperarm_R: 'clavicle_R', forearm_R: 'upperarm_R', hand_R: 'forearm_R', thigh_L: 'pelvis', calf_L: 'thigh_L', foot_L: 'calf_L', toe_L: 'foot_L',
  thigh_R: 'pelvis', calf_R: 'thigh_R', foot_R: 'calf_R', toe_R: 'foot_R',
};

/** Optional chains in §B3 priority order. */
export type ChainName = 'tail' | 'shell' | 'cloth' | 'skirt' | 'wings' | 'weapon' | 'beard' | 'hat';

/** Derived body dimensions shared by the mesh generator and the landmarks (raw, pre-normalization units). */
export interface BodyDims {
  hH: number; hW: number; sx: number; sy: number; sz: number; kT: number; kL: number; kA: number;
  chin: number; crown: number; headZ: number;
  /** Half-width / half-depth of the skull at cheek level. */
  skullRx: number; skullRz: number;
  /** z of the flat face plane at the eyes. */
  faceZ: number;
  eyeR: number; eyeX: number; eyeY: number; eyeZ: number; mouthY: number;
  armR: number; legR: number; tailR: number; neckR: number;
  /** Torso ring radius scale (× chestW etc. applied in the mesh). */
  chestW: number; hipW: number; belly: number;
  /** Paw length (wrist → fingertips) and foot length. */
  pawLen: number; footLen: number;
  digitigrade: boolean;
}

export interface BuiltRig {
  readonly rig: Rig;
  /** Model-space pivots by bone name (normalized, H). */
  readonly pivots: Readonly<Partial<Record<BoneName, V3>>>;
  /** The same pivots before normalization (the mesh is built in these units, then scaled by `norm`). */
  readonly raw: Readonly<Partial<Record<BoneName, V3>>>;
  readonly dims: BodyDims;
  /** Normalization factor raw → H (crown = 1.0). Mesh vertices are built raw and multiplied by it. */
  readonly norm: number;
  /** Chain bone indices (empty when absent or rigid). */
  readonly chains: Readonly<Record<ChainName, readonly number[]>>;
  /** Trophy sockets (creatures §B2; not in the contract's SocketName yet — see the hand-off). */
  readonly trophies: Readonly<Record<'trophy_hat' | 'trophy_belt' | 'trophy_back', { bone: number; offset: V3 }>>;
  /** Tail rest centreline (normalized), root → tip, for the mesh and QA ("never straight"). */
  readonly tailPath: readonly V3[];
  /** Headwear top height above the crown (H), for `hatTop`. */
  readonly hatHeight: number;
  /** Face rig data for `faceToBones()` (eyes, base face). */
  readonly faceInfo: FaceRigInfo;
  /** Skull profile (raw units) shared with the head mesh. */
  readonly skull: SkullProfile;
  /** Raw eye geometry (L, R). */
  readonly eyes: readonly [EyeGeom, EyeGeom];
}

export interface RigOptions {
  /** A chain weapon is equipped (flail, censer, dead rat…; §B3 priority 4). */
  weaponChain?: boolean;
}

const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scl = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const mirror = (a: V3): V3 => [-a[0], a[1], a[2]];
const DEG = Math.PI / 180;

/** Ear rest directions for the left ear (creature's left = +X) by ear option; the right ear mirrors X. */
const EAR_DIR: Readonly<Record<string, V3>> = {
  triangle: [0.18, 1, -0.06], tall: [0.12, 1, -0.05], rounded: [0.3, 1, -0.02], folded: [0.35, 1, 0.12], lynx: [0.15, 1, -0.06], torn: [0.2, 1, -0.06],
  mouse: [0.7, 1, -0.05], long: [0.1, 1, -0.1], lop: [0.85, -0.45, 0.05], hound: [0.35, -1, 0.12], goat: [1, 0.05, -0.1], owlTufts: [0.35, 1, -0.08],
  none: [0.2, 1, 0], stalks: [0.22, 1, 0.12], human: [1, 0.1, -0.15],
};
/** Ear length multipliers (long ears ×2; stalks are tall). */
const EAR_LEN: Readonly<Record<string, number>> = { long: 2, lop: 1.7, hound: 1.3, mouse: 1.15, stalks: 1.5, goat: 0.9, owlTufts: 0.9, human: 0.7, rounded: 0.8, folded: 0.9 };
/** Where on the head the ear base sits (fraction of head height above the chin) and its x scale. */
const EAR_BASE: Readonly<Record<string, [y: number, xMul: number]>> = {
  lop: [0.8, 1.05], hound: [0.8, 1.05], goat: [0.72, 1.1], human: [0.5, 1.12], mouse: [0.82, 1], stalks: [0.92, 0.6],
};

/** Torso ring radii (× T) per torso option, hip → neck (creatures §B7.3). */
export const TORSO_RINGS: Readonly<Record<string, readonly number[]>> = {
  // the canon pear pushed heavier at the bottom (concept INDEX: "push the pear further than drawn")
  pear: [0.118, 0.13, 0.122, 0.104, 0.088, 0.073, 0.046],
  barrel: [0.10, 0.123, 0.134, 0.129, 0.112, 0.095, 0.046],
  vshape: [0.078, 0.084, 0.095, 0.112, 0.123, 0.118, 0.05],
  stick: [0.067, 0.065, 0.062, 0.062, 0.063, 0.062, 0.04],
  egg: [0.095, 0.112, 0.118, 0.112, 0.1, 0.078, 0.045],
  hourglass: [0.106, 0.1, 0.073, 0.084, 0.106, 0.1, 0.045],
  hunched: [0.1, 0.106, 0.106, 0.1, 0.095, 0.084, 0.05],
  boxy: [0.106, 0.112, 0.112, 0.112, 0.11, 0.1, 0.05],
};

/** Builds the creature skeleton from a resolved spec. */
export function buildRig(spec: CreatureSpec, opts: RigOptions = {}): BuiltRig {
  const v = spec.v;
  const o = spec.o;
  const L = v.legLen, T = v.torsoLen;
  const kT = T / 0.36, kL = L / 0.33;
  let kA = v.armLen / 0.40;
  const hH = v.headH, hW = v.headW;
  const sx = hW / 0.27, sy = hH / 0.31, sz = 0.5 + 0.5 * sx;
  const digitigrade = o.legType === 'digitigrade' && !spec.flags.slugBody;
  const P: Partial<Record<BoneName, V3>> = {};

  // Spine (§B2, §B4: pelvis y = 0.03 + L; torso offsets × T/0.36).
  const pelvisY = 0.03 + L;
  P.pelvis = [0, pelvisY, 0];
  P.spine_01 = [0, pelvisY + 0.08 * kT, 0];
  P.spine_02 = [0, pelvisY + 0.19 * kT, 0];
  P.belly = [0, pelvisY + 0.09 * kT, 0.06 * (0.7 + 0.6 * v.bellySize)];
  P.neck = [0, pelvisY + 0.28 * kT, 0.005];
  const chin = P.neck[1] + v.neckLen;
  const headZ = 0.015 + 0.6 * (v.neckLen - 0.05) * (o.posture === 'bull' ? 1 : 0);
  P.head = [0, chin, headZ];
  const crown = chin + hH;

  // Face (§B4: head offsets × headH/0.31 in y, headW/0.27 in x). Eyes, lids and brows sit on the actual skull surface.
  const muz = v.muzzle - 0.03;
  const faceZ = headZ + 0.09 * sz;
  const skullRx = 0.135 * sx * (o.skull === 'pancake' ? 1.15 : o.skull === 'wedge' ? 0.95 : 1);
  const skullRz = 0.105 * sz;
  const eyeR = (v.eyeSize * hW) / 2;
  const eyeX = (v.eyeSpacing * hW) / 2;
  const eyeY = chin + v.eyeHeight * hH;
  const profile = skullProfile(spec, { chin, hH, headZ, skullRx, skullRz, faceZ, sz });
  const preDims = { eyeR, eyeX, eyeY, skullRx } as BodyDims;
  const eyeL = eyeGeom(spec, preDims, 1, profile.front(eyeX, eyeY));
  const eyeRg = eyeGeom(spec, preDims, -1, profile.front(-eyeX, eyeY));
  const mouthY = chin + 0.055 * sy * (spec.head.id === 'toad' ? 1.4 : 1);
  P.jaw = [0, chin + 0.04 * sy, headZ + 0.035 * sz + 0.4 * muz];
  P.mouth = [0, mouthY, profile.front(0, mouthY) + 0.01 + muz];
  P.nose = [0, chin + 0.11 * sy, profile.front(0, chin + 0.11 * sy) + 0.016 + muz];
  P.eye_L = eyeL.center;
  P.eye_R = eyeRg.center;
  // Lid pivot = the axis of the lid cylinder behind the eye (the lid shell is concentric with it; mesh/head.ts).
  P.lid_L = eyeL.lidPivot;
  P.lid_R = eyeRg.lidPivot;
  const browY = eyeY + eyeL.ry * 1.45 + 0.004 + v.browHeight * 0.012;
  const browX = eyeX + 0.003;
  P.brow_L = [browX, browY, profile.front(browX, browY) + 0.004];
  P.brow_R = [-browX, browY, profile.front(-browX, browY) + 0.004];

  // Ears (§B4: base x = ±earSet·headW/2; length = earSize; rest tilt; ear_R_02 droops by earAsym).
  const earType = spec.flags.eyeStalks ? 'stalks' : o.ears;
  const [baseYf, baseXm] = EAR_BASE[earType] ?? [0.855, 1];
  const earRing = profile.ring(baseYf);
  const earX = Math.min((v.earSet * hW / 2) * baseXm, earRing.rs * (earType === 'goat' || earType === 'human' || earType === 'lop' || earType === 'hound' ? 0.98 : 0.9));
  const earY = earRing.y;
  const earLen = v.earSize * (EAR_LEN[earType] ?? 1) * (spec.head.id === 'donkey' && earType === 'long' ? 0.8 : 1);
  const earBaseL: V3 = [earX, earY, earRing.cz - 0.2 * earRing.rf];
  if (earType === 'stalks') earBaseL[2] = earRing.cz + 0.1 * earRing.rf;
  const dir = norm3(EAR_DIR[earType] ?? EAR_DIR.triangle);
  const tilt = (d: V3, deg: number): V3 => { // rotate about X: + tilts the tip back
    const a = -deg * DEG; return [d[0], d[1] * Math.cos(a) - d[2] * Math.sin(a), d[1] * Math.sin(a) + d[2] * Math.cos(a)];
  };
  const dL = tilt(dir, v.earTilt);
  const dR = tilt(mirror(dir), v.earTilt - v.earAsym);
  P.ear_L_01 = earBaseL;
  P.ear_L_02 = add(earBaseL, scl(dL, earLen * 0.75));
  P.ear_R_01 = mirror(earBaseL);
  P.ear_R_02 = add(mirror(earBaseL), scl(dR, earLen * 0.75));

  // Arms (§B4: clavicle→upperarm x × shoulderW; offsets × A/0.40).
  if (o.arms === 'trex') kA = Math.min(kA, 0.8);
  const foreMul = o.arms === 'trex' ? 0.55 : 1;
  const neckY = P.neck[1];
  const clav: V3 = [0.02, neckY - 0.015 * kT, 0];
  const upper: V3 = [0.115 * v.shoulderW, neckY - 0.04 * kT, 0];
  const fore: V3 = add(upper, [0.01, -0.16 * kA, 0]);
  const hand: V3 = add(fore, [0.005, -0.17 * kA * foreMul, 0.012]);
  P.clavicle_L = clav; P.upperarm_L = upper; P.forearm_L = fore; P.hand_L = hand;
  P.clavicle_R = mirror(clav); P.upperarm_R = mirror(upper); P.forearm_R = mirror(fore); P.hand_R = mirror(hand);

  // Legs (§B4: thigh→calf→foot × L/0.33; pelvis→thigh x × hipW·stanceW; digitigrade variant §B2).
  const hx = 0.065 * v.hipW * v.stanceW;
  const legShapeX = o.legs === 'bowlegged' ? 0.03 : o.legs === 'knock' ? -0.022 : 0;
  const fs = v.footSize / 0.11;
  let thigh: V3, calf: V3, foot: V3, toe: V3;
  if (spec.flags.slugBody) {
    // Legs hidden under the foot-skirt: bones stay (the skirt undulation reads the thighs, §B7.3).
    thigh = [hx * 0.8, pelvisY - 0.03, 0]; calf = [hx * 0.8, pelvisY * 0.55, 0.02]; foot = [hx * 0.8, 0.06, 0.02]; toe = [hx * 0.8, 0.012, 0.09];
  } else if (digitigrade) {
    thigh = [hx, pelvisY - 0.03, 0];
    calf = [hx + 0.003 + legShapeX, 0.205 * kL, 0.040];
    foot = [hx + 0.005, 0.090 * kL, -0.050];
    toe = [hx + 0.005, 0.012, -0.050 + 0.095 * fs];
  } else {
    thigh = [hx, pelvisY - 0.03, 0];
    calf = [hx + 0.003 + legShapeX, thigh[1] - 0.15 * kL, 0.012];
    foot = [hx + 0.005, calf[1] - 0.135 * kL, -0.010];
    toe = [hx + 0.005, 0.012, -0.010 + 0.085 * fs];
  }
  P.thigh_L = thigh; P.calf_L = calf; P.foot_L = foot; P.toe_L = toe;
  P.thigh_R = mirror(thigh); P.calf_R = mirror(calf); P.foot_R = mirror(foot); P.toe_R = mirror(toe);

  // ── Bones (core) ──
  const bones: BoneDef[] = [];
  const index: Partial<Record<BoneName, number>> = {};
  const push = (name: BoneName, parent: BoneName | null, pivot: V3): number => {
    const i = bones.length;
    bones.push({ name, parent: parent === null ? -1 : index[parent]!, pivot });
    index[name] = i;
    return i;
  };
  for (const b of CORE_BONES) push(b, CORE_PARENT[b] as BoneName | null, P[b]!);

  // ── Optional chains (§B3 priority order, 13 slots) ──
  const chains: Record<ChainName, number[]> = { tail: [], shell: [], cloth: [], skirt: [], wings: [], weapon: [], beard: [], hat: [] };
  const rigidChains: string[] = [];
  const room = (): number => MAX_BONES - bones.length;
  const tryChain = (name: ChainName, defs: Array<[BoneName, BoneName | null, V3]>): boolean => {
    if (defs.length === 0) return true;
    if (defs.length > room()) { rigidChains.push(name); return false; }
    for (const [bn, parent, piv] of defs) {
      P[bn] = piv;
      chains[name].push(push(bn, parent, piv));
    }
    return true;
  };

  // 1. Tail (or the snail shell).
  const tailPath: V3[] = [];
  if (spec.flags.slugBody) {
    tryChain('shell', [['shell', 'spine_02', [0, P.spine_02[1] - 0.01 * kT, -0.09 - 0.02 * (v.chestW - 1)]]]);
  } else if (spec.flags.hasTail) {
    const t = o.tail;
    const N = t === 'stub' || t === 'pom' || t === 'feather' ? 1 : t === 'endless' ? 5 : Math.max(3, Math.min(5, Math.round(3 + (2 * (v.tailLen - 0.8)) / 0.7)));
    const len = t === 'stub' ? 0.08 : t === 'pom' ? 0.05 : t === 'feather' ? 0.1 : t === 'endless' ? 1.8 : t === 'corkscrew' ? 0.3 * v.tailLen : t === 'bushy' ? 0.85 * v.tailLen : v.tailLen;
    const root: V3 = [0, pelvisY - 0.01, -0.07 * v.hipW - 0.01];
    const seg = len / N;
    const defs: Array<[BoneName, BoneName | null, V3]> = [];
    let p: V3 = root;
    tailPath.push(root);
    // Rest curve (creatures §B4.1, art rule 4: S or J, never straight): out and down from the rump, then sweeping up into
    // the question-mark hook (absolute pitch from −60° to +150°; tailCurl adds ±30°). Rat tails lie in a long low S,
    // bushy tails hang in a J, corkscrews coil, the absurd tail loops.
    const sweep = 210 + 30 * v.tailCurl;
    for (let i = 0; i < N; i++) {
      const u = N === 1 ? 0 : i / (N - 1);
      let pitch = N === 1 ? 20 : -60 + sweep * u;
      let yaw = 0;
      if (t === 'hooked' && i === N - 1) pitch += 60;
      if (t === 'rat') pitch = -35 + 120 * u - 70 * Math.sin(u * Math.PI);
      if (t === 'endless') pitch = -40 + 300 * u;
      if (t === 'bushy') pitch = -70 + 140 * u;
      if (t === 'tufted') pitch = -65 + 175 * u;
      if (t === 'corkscrew') { pitch = 10 + 40 * u; yaw = 70 * (i + 1); }
      if (t === 'endless') yaw = i % 2 === 0 ? 30 : -30;
      const pr = pitch * DEG, yr = yaw * DEG;
      const d: V3 = [Math.sin(yr) * Math.cos(pr), Math.sin(pr), -Math.cos(yr) * Math.cos(pr)];
      defs.push([`tail_${(i + 1) as 1 | 2 | 3 | 4 | 5}`, i === 0 ? 'pelvis' : `tail_${i as 1 | 2 | 3 | 4}`, p]);
      p = add(p, scl(d, seg));
      tailPath.push(p);
    }
    tryChain('tail', defs);
  }

  // 2. Cloth: capelet 2 (L, R); long cape / count's cape / tattered / crusader / ermine / blanket 4 (2 × 2); robe skirts 4.
  const back: V3 = [0, P.spine_02[1] + 0.02 * kT, -0.085 * Math.max(1, v.chestW)];
  const capeKind = o.cape;
  if (capeKind === 'capelet') {
    tryChain('cloth', [['cloth_0', 'spine_02', add(back, [0.06, -0.02, 0])], ['cloth_1', 'spine_02', add(back, [-0.06, -0.02, 0])]]);
  } else if (capeKind !== 'none') {
    const lowY = Math.max(0.12, pelvisY - 0.12);
    tryChain('cloth', [
      ['cloth_0', 'spine_02', add(back, [0.05, -0.03, -0.01])], ['cloth_1', 'spine_02', add(back, [-0.05, -0.03, -0.01])],
      ['cloth_2', 'cloth_0', [0.07, lowY, back[2] - 0.05]], ['cloth_3', 'cloth_1', [-0.07, lowY, back[2] - 0.05]],
    ]);
  }
  const longGarment = ['robe', 'alb', 'chasuble', 'ermine', 'motley', 'execTunic'].includes(o.garment);
  // The robe skirt shares the cloth chain (§B3 "cape / robe skirt"): it gets its own 4 bones only without a long cape;
  // otherwise it is skinned 50 % pelvis / 50 % thighs (§B7.4).
  if (longGarment && !spec.flags.slugBody && (capeKind === 'none' || capeKind === 'capelet')) {
    const hemY = Math.max(0.06, pelvisY * 0.35);
    tryChain('skirt', [
      ['cloth_4', 'pelvis', [0.06, pelvisY - 0.08, 0.07]], ['cloth_5', 'pelvis', [-0.06, pelvisY - 0.08, 0.07]],
      ['cloth_6', 'cloth_4', [0.08, hemY, 0.06]], ['cloth_7', 'cloth_5', [-0.08, hemY, 0.06]],
    ]);
  }
  // 3. Wings (not wing-arms).
  if (o.wings !== 'none') tryChain('wings', [['wing_L', 'spine_02', add(back, [0.05, 0.02, -0.01])], ['wing_R', 'spine_02', add(back, [-0.05, 0.02, -0.01])]]);
  // 4. Weapon chain.
  if (opts.weaponChain) {
    const grip = add(P.hand_R!, [0, -0.035 * kA, 0.008]);
    tryChain('weapon', [['weapon_1', 'hand_R', grip], ['weapon_2', 'weapon_1', add(grip, [0, -0.12, 0.02])]]);
  }
  // 5. Beard chain (beards 2, 3, 5, 9).
  if (['monk', 'forked', 'braided', 'waist'].includes(o.beard) && !spec.flags.beardHidden) {
    tryChain('beard', [['beard', 'jaw', [0, chin + 0.012, P.jaw[2] + 0.03]]]);
  }
  // 6. Hat chain (jester bells, plume, hennin veil, bascinet visor).
  const hatHeight = HAT_HEIGHT[o.headwear] ?? 0;
  if (['jester', 'hennin', 'bascinet', 'pilgrim'].includes(o.headwear)) {
    const hp: V3 = o.headwear === 'bascinet' ? [0, chin + 0.62 * hH, faceZ + 0.01] : [0, crown + hatHeight * 0.6, headZ - 0.05];
    tryChain('hat', [['hat_1', 'head', hp]]);
  }

  // ── Normalize to crown = 1.0 ──
  const norm = 1 / crown;
  for (const b of bones) b.pivot = scl(b.pivot, norm);
  const pivots: Partial<Record<BoneName, V3>> = {};
  for (const b of bones) pivots[b.name] = b.pivot;
  for (const k of Object.keys(P) as BoneName[]) if (!pivots[k]) pivots[k] = scl(P[k]!, norm);

  // ── Sockets (§B2 «S» rows; offset relative to the parent pivot, identity rotation) ──
  const sock = (bone: BoneName, at: V3): { bone: number; offset: V3; rot: V3 } => {
    const i = index[bone]!;
    const p = bones[i].pivot;
    const a = scl(at, norm);
    return { bone: i, offset: [a[0] - p[0], a[1] - p[1], a[2] - p[2]], rot: [0, 0, 0] };
  };
  const sockets: Partial<Record<SocketName, { bone: number; offset: V3; rot: V3 }>> = {
    hat: sock('head', [0, crown, headZ - 0.005]),
    prop_L: sock('hand_L', add(hand, [0, -0.035 * kA, 0.008])),
    prop_R: sock('hand_R', add(mirror(hand), [0, -0.035 * kA, 0.008])),
    back: sock('spine_02', [0, P.spine_02[1] + 0.01, -0.075 * Math.max(1, v.chestW)]),
    belt_L: sock('pelvis', [0.085 * v.hipW * (1 + 0.3 * v.bellySize), pelvisY, 0.04]),
    belt_R: sock('pelvis', [-0.085 * v.hipW * (1 + 0.3 * v.bellySize), pelvisY, 0.04]),
    chest: sock('spine_02', [0, P.spine_02[1] + 0.05 * kT, 0.07 * v.chestW + 0.01]),
  };
  const trophyAt = (bone: BoneName, at: V3): { bone: number; offset: V3 } => { const s = sock(bone, at); return { bone: s.bone, offset: s.offset }; };
  const trophies = {
    trophy_hat: trophyAt('head', [0, crown + hatHeight + 0.01, headZ - 0.025]),
    trophy_belt: trophyAt('pelvis', [0.095 * v.hipW * (1 + 0.3 * v.bellySize), pelvisY - 0.02, 0.03]),
    trophy_back: trophyAt('spine_02', [0, P.spine_02[1] + 0.05 * kT, -0.11 * Math.max(1, v.chestW)]),
  };

  const legBase = 0.040 * v.legThick * (o.legs === 'stilts' ? 0.7 : o.legs === 'hams' ? 1.3 : o.legs === 'stubby' ? 1.2 : 1);
  const eyeZ = eyeL.center[2];
  const dims: BodyDims = {
    hH, hW, sx, sy, sz, kT, kL, kA, chin, crown, headZ, skullRx, skullRz, faceZ, eyeR, eyeX, eyeY, eyeZ, mouthY,
    armR: 0.032 * v.armThick * (o.arms === 'beefy' ? 1.35 : o.arms === 'spindly' ? 0.72 : 1),
    legR: legBase,
    tailR: v.tailThick * 1.2, neckR: 0.056 * Math.max(0.8, Math.min(1.25, sx)),
    chestW: v.chestW, hipW: v.hipW, belly: v.bellySize,
    pawLen: 0.072 * kA * v.handSize, footLen: v.footSize, digitigrade,
  };
  const rig: Rig = { kind: 'biped', bones, index, sockets, rigidChains };
  const mouthHalfW = 0.15 * hW * v.mouthW * (o.mouth === 'toad' ? 1.6 : o.mouth === 'o' ? 0.35 : 1);
  const faceInfo: FaceRigInfo = { eyes: [eyeL, eyeRg], base: baseFace(spec), mouthHalfW, norm };
  return { rig, pivots, raw: P, dims, norm, chains, trophies, tailPath: tailPath.map((p) => scl(p, norm)), hatHeight: hatHeight * norm,
    faceInfo, skull: profile, eyes: [eyeL, eyeRg] };
}

/** Headwear heights above the crown (raw units), for `hatTop` and the trophy socket. */
export const HAT_HEIGHT: Readonly<Record<string, number>> = {
  none: 0, crown: 0.07, circlet: 0.02, mitre: 0.2, greatHelm: 0.05, kettle: 0.05, bascinet: 0.09, cowl: 0.04, chaperon: 0.06, jester: 0.14, laurel: 0.02,
  haloDisc: 0.12, pilgrim: 0.07, astrologer: 0.26, coif: 0.02, beret: 0.04, hennin: 0.32, pot: 0.08, plagueMask: 0.08, execHood: 0.18, daisies: 0.02,
  nemesisHat: 0.09,
};

function norm3(a: V3): V3 { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }

/** Rest-pose world (= model) position of a bone pivot. */
export const pivotOf = (b: BuiltRig, name: BoneName): V3 => b.pivots[name]!;
